import { useEffect, useState } from 'react';
import { ResolveMeeting, ResolvedMeeting } from '../../connection/health';

export enum ResolveState {
	Pending = 'pending',
	Resolved = 'resolved',
	NotFound = 'notFound',
	Forbidden = 'forbidden',
	Failed = 'failed',
}

interface UseResolvedRoomResult {
	state: ResolveState;
	meeting: ResolvedMeeting | null;
	error?: string; /** Populated when state is Failed. */
}

/**
 * Resolves a producer url slug to the numeric room id the sync routes need.
 * Runs once per slug rather than on every health poll - the mapping does not
 * change while the dashboard is open.
 */
export default function useResolvedRoom(
	slug: string | undefined,
	token: string | null,
): UseResolvedRoomResult {
	const [result, setResult] = useState<UseResolvedRoomResult>({
		state: ResolveState.Pending,
		meeting: null,
	});

	useEffect(() => {
		if (!slug || !token) {
			setResult({ state: ResolveState.Pending, meeting: null });
			return undefined;
		}

		let active = true;
		setResult({ state: ResolveState.Pending, meeting: null });

		ResolveMeeting(slug, token).then(({ httpStatus, meeting, error }) => {
			if (!active) return;

			if (meeting) {
				setResult({ state: ResolveState.Resolved, meeting });
				return;
			}

			if (httpStatus === 401 || httpStatus === 403) {
				setResult({ state: ResolveState.Forbidden, meeting: null });
				return;
			}

			if (httpStatus === 200 || httpStatus === 404) {
				setResult({ state: ResolveState.NotFound, meeting: null });
				return;
			}

			setResult({
				state: ResolveState.Failed,
				meeting: null,
				error: error ?? `lookup failed (${httpStatus})`,
			});
		});

		return () => { active = false; };
	}, [slug, token]);

	return result;
}
